import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageSquarePlus, Bug, Lightbulb, X, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../config';

const TYPES = [
  { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
  { value: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
];

const PAGE_LABELS = {
  '/': 'Home Dashboard',
  '/intake': 'Claims Intake',
  '/all-entries': 'All Entries',
  '/companies': 'Company Entries',
  '/admin': 'Company Management',
  '/settings': 'Settings',
  '/recycle-bin': 'Recycle Bin',
  '/user-management': 'User Management',
  '/audit-log': 'Audit Log',
  '/support': 'Support Queue',
  '/organizations': 'Organizations',
};

export default function FeedbackButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('suggestion');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [capturedPath, setCapturedPath] = useState('');

  const reset = () => { setType('suggestion'); setTitle(''); setDescription(''); };

  const openModal = () => {
    setCapturedPath(location.pathname);
    setOpen(true);
  };

  // Allow other components (e.g. mobile bottom-nav "More" drawer) to open the
  // feedback modal by dispatching a global `open-feedback` window event. This
  // lets us hide the floating FAB on mobile (where it blocks tap targets on
  // /intake forms and the bottom-nav) while still keeping feedback one tap
  // away inside the More drawer.
  useEffect(() => {
    const handler = () => openModal();
    window.addEventListener('open-feedback', handler);
    return () => window.removeEventListener('open-feedback', handler);
    // openModal is stable enough — it only reads location.pathname inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageLabel = PAGE_LABELS[capturedPath] || capturedPath;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        type, title: title.trim(), description: description.trim(),
        page_url: capturedPath,
      }, { withCredentials: true });
      toast.success('Thank you! Your feedback has been submitted.');
      reset();
      setOpen(false);
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button — desktop only. On mobile (< md) the FAB blocked
       *  bottom-nav tap targets and form buttons, so we hide it here and let
       *  the bottom-nav "More" drawer dispatch `open-feedback` to surface
       *  this modal instead. */}
      <button
        onClick={openModal}
        className="hidden md:flex fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-600/30 items-center justify-center transition-all hover:scale-105 active:scale-95"
        data-testid="feedback-fab"
        title="Send Feedback"
      >
        <MessageSquarePlus className="w-5 h-5" />
      </button>

      {/* Modal Overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="fixed inset-0 bg-black/40" />
          <form
            onSubmit={handleSubmit}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
            data-testid="feedback-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="text-base font-semibold text-gray-900">Send Feedback</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600" data-testid="feedback-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-4">
              {/* Type Toggle */}
              <div className="flex gap-2">
                {TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-all ${
                      type === t.value ? t.bg + ' ' + t.color : 'border-gray-200 text-gray-400 hover:border-gray-300'
                    }`}
                    data-testid={`feedback-type-${t.value}`}
                  >
                    <t.icon className="w-4 h-4" />
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Title */}
              <input
                type="text"
                placeholder={type === 'bug' ? 'What went wrong?' : 'Your idea in one line'}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                required
                data-testid="feedback-title"
              />

              {/* Description */}
              <textarea
                placeholder="Add details (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 resize-none"
                data-testid="feedback-description"
              />

              {/* Page context */}
              <p className="text-[11px] text-gray-400">
                Submitting from: <span className="text-gray-500 font-medium">{pageLabel}</span>
              </p>

              {/* Submit */}
              <button
                type="submit"
                disabled={!title.trim() || submitting}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
                data-testid="feedback-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
